// The Warm Isles: the open warm sea south-west of the Middle Sea, and the
// islands in it. Turquoise shallows with coral, white beaches with shells,
// coconut palms leaning over the water, a green interior with hibiscus, and
// on the biggest island a volcano with lava in its crater. One is an atoll: a
// ring of sand round a lagoon. Also the Square's new south-west beach. No
// places yet: this is land (and water) to wander, reached by ferry.
//
// Its own random streams (seed + 700 and up), consumed in full whatever region
// is being painted.
import { rng, noise2, softShadow, wash, blobPath, inkPoly, inkEllipse, line, withAlpha, mix, hatch, samplePath } from "./paint"
import { paintTuft, paintBush, paintRock } from "./nature"

export function buildIsles(config, { x0, y0, x1, y1 }) {
    const seed = config.seed
    const active = !!config.sections?.some((s) => s.key === "isles")
    const n = noise2(seed + 700)
    const islands = active ? (config.warm_isles || []).map((isl, i) => ({ ...isl, seed: seed + 710 + i })) : []
    const shore = active && config.west_coast ? samplePath(config.west_coast, 8) : []
    const inEllipse = (e, x, y, pad = 0) => ((x - e.x) / Math.max(1, e.rx - pad)) ** 2 + ((y - e.y) / Math.max(1, e.ry - pad)) ** 2 < 1
    const inLagoon = (isl, x, y, pad = 0) => !!isl.lagoon && inEllipse({ x: isl.x, y: isl.y, rx: isl.lagoon.rx, ry: isl.lagoon.ry }, x, y, -pad)
    // land: inside the island, outside its lagoon
    const islandAt = (x, y, pad = 0) => islands.find((isl) => inEllipse(isl, x, y, pad) && !inLagoon(isl, x, y, pad))
    const inWater = (x, y, pad = 0) => islands.some((isl) => inLagoon(isl, x, y, -pad))
    const volcanoes = islands.filter((isl) => isl.volcano).map((isl) => ({ ...isl.volcano, seed: isl.seed + 5 }))
    const nearVolcano = (x, y, pad = 0) => volcanoes.some((v) => Math.hypot(v.x - x, (v.y - y) * 1.3) < v.s * 0.95 + pad)

    function biome(x, y) {
        return islandAt(x, y, -20) ? "tropical" : "meadow"
    }

    // --- the Square's south-west beach ---------------------------------------------

    function paintShore(ctx, pal, vis) {
        if (!shore.length || !shore.some((p) => vis(p.x, p.y, 200))) return
        ctx.save()
        ctx.lineCap = "round"; ctx.lineJoin = "round"
        ;[[250, pal.tropSand], [90, pal.tropSandDark]].forEach(([width, color]) => {
            ctx.strokeStyle = color; ctx.lineWidth = width
            ctx.beginPath(); shore.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke()
        })
        ctx.restore()
    }

    // --- after the sea: warmer water, then the islands ----------------------------------

    function paintAfterSea(ctx, pal, seaPolygon, vis) {
        if (!active || !seaPolygon) return
        // the warm sea: brighter and greener toward the isles
        if (vis((x0 + x1) / 2, (y0 + y1) / 2, Math.max(x1 - x0, y1 - y0))) {
            ctx.save()
            ctx.beginPath(); seaPolygon.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
            const cx = x0 + (x1 - x0) * 0.5, cy = y0 + (y1 - y0) * 0.55
            const g = ctx.createRadialGradient(cx, cy, 200, cx, cy, 3800)
            g.addColorStop(0, withAlpha(pal.warmSea, 0.55)); g.addColorStop(0.7, withAlpha(pal.warmSea, 0.3)); g.addColorStop(1, withAlpha(pal.warmSea, 0))
            ctx.fillStyle = g; ctx.fillRect(cx - 3800, cy - 3800, 7600, 7600)
            paintSwell(ctx, pal, vis)
            ctx.restore()
        }
        // sand grain and shells on the Square's new beach
        const rb = rng(seed + 701)
        shore.forEach((p) => {
            for (let k = 0; k < 5; k++) {
                const o = 10 + rb() * 110, jitter = (rb() - 0.5) * 16, size = 0.8 + rb() * 1.4, kind = rb()
                if (!vis(p.x, p.y, 130)) continue
                const x = p.x - p.nx * o + p.ny * jitter, y = p.y - p.ny * o - p.nx * jitter
                ctx.fillStyle = kind < 0.1 ? "#F6E7DA" : kind < 0.16 ? pal.pebble : withAlpha(pal.tropSandDark, 0.6)
                ctx.beginPath(); ctx.arc(x, y, kind < 0.16 ? size + 1.2 : size, 0, Math.PI * 2); ctx.fill()
            }
        })
        islands.forEach((isl) => paintIsland(ctx, pal, isl, vis))
    }

    // The open water's texture: pale shoals here and there, and the glint of
    // small waves with a darker trough under each. Every cell's marks come
    // from a hash of the cell, so any tile paints the same sea.
    function paintSwell(ctx, pal, vis) {
        const cellHash = (cx, cy, k) => { let h = (cx * 73856093) ^ (cy * 19349663) ^ (k * 83492791) ^ seed; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296 }
        const S = 700
        for (let cx = Math.floor(x0 / S); cx <= Math.floor(x1 / S); cx++) {
            for (let cy = Math.floor(y0 / S); cy <= Math.floor(y1 / S); cy++) {
                if (cellHash(cx, cy, 1) > 0.22) continue
                const x = cx * S + cellHash(cx, cy, 2) * S, y = cy * S + cellHash(cx, cy, 3) * S, rx = 90 + cellHash(cx, cy, 4) * 140
                if (!vis(x, y, rx + 20) || islandAt(x, y, 260)) continue
                blobPath(ctx, x, y, rx, rx * 0.45, seed + cx * 31 + cy, 0.12, 20)
                ctx.fillStyle = withAlpha(pal.lagoon, 0.22); ctx.fill()
                blobPath(ctx, x, y, rx * 0.55, rx * 0.22, seed + cx * 31 + cy + 7, 0.14, 16)
                ctx.fillStyle = withAlpha(pal.seaShallow, 0.3); ctx.fill()
            }
        }
        const G = 95
        ctx.lineCap = "round"
        for (let cx = Math.floor(x0 / G); cx <= Math.floor(x1 / G); cx++) {
            for (let cy = Math.floor(y0 / G); cy <= Math.floor(y1 / G); cy++) {
                if (cellHash(cx, cy, 5) > 0.5) continue
                const x = cx * G + cellHash(cx, cy, 6) * G, y = cy * G + cellHash(cx, cy, 7) * G, len = 7 + cellHash(cx, cy, 8) * 14
                if (!vis(x, y, len + 4) || islandAt(x, y, 150)) continue
                ctx.strokeStyle = withAlpha(pal.seaDeep, 0.16); ctx.lineWidth = 2
                ctx.beginPath(); ctx.moveTo(x - len, y + 3); ctx.quadraticCurveTo(x, y + 6, x + len, y + 3); ctx.stroke()
                ctx.strokeStyle = withAlpha(pal.foam, 0.2 + cellHash(cx, cy, 9) * 0.25); ctx.lineWidth = 1.4
                ctx.beginPath(); ctx.moveTo(x - len, y); ctx.quadraticCurveTo(x, y - 4, x + len, y); ctx.stroke()
            }
        }
    }

    function paintIsland(ctx, pal, isl, vis) {
        const r = rng(isl.seed)
        const reefs = Array.from({ length: 34 }, () => [r(), r(), r(), r()])
        const texture = Array.from({ length: 700 }, () => [r(), r(), r()])
        if (!vis(isl.x, isl.y, Math.max(isl.rx, isl.ry) + 220)) return
        // turquoise shallows, and coral scattered in them
        blobPath(ctx, isl.x, isl.y, isl.rx + 170, isl.ry + 130, isl.seed, 0.08, 40)
        ctx.fillStyle = withAlpha(pal.lagoon, 0.55); ctx.fill()
        blobPath(ctx, isl.x, isl.y, isl.rx + 80, isl.ry + 60, isl.seed + 1, 0.07, 40)
        ctx.fillStyle = withAlpha(pal.lagoon, 0.75); ctx.fill()
        reefs.forEach(([a, d, c, s]) => {
            const ang = a * Math.PI * 2, k = 1.05 + d * 0.25
            const x = isl.x + Math.cos(ang) * isl.rx * k, y = isl.y + Math.sin(ang) * isl.ry * k
            blobPath(ctx, x, y, 12 + s * 22, 7 + s * 12, isl.seed + 50 + Math.floor(c * 100), 0.3, 12)
            ctx.fillStyle = withAlpha(c < 0.5 ? pal.reef : pal.reef2, 0.55); ctx.fill()
        })
        // the beach, a line of surf, then the green interior
        blobPath(ctx, isl.x, isl.y, isl.rx + 26, isl.ry + 20, isl.seed + 1, 0.07, 40)
        ctx.fillStyle = withAlpha(pal.foam, 0.6); ctx.fill()
        blobPath(ctx, isl.x, isl.y, isl.rx + 10, isl.ry + 8, isl.seed + 1, 0.07, 40)
        ctx.fillStyle = pal.tropSand; ctx.fill()
        ctx.strokeStyle = pal.foam; ctx.lineWidth = 3; ctx.globalAlpha = 0.9; ctx.stroke(); ctx.globalAlpha = 1
        ctx.save()
        blobPath(ctx, isl.x, isl.y, isl.rx - 60, isl.ry - 48, isl.seed + 2, 0.1, 40)
        ctx.fillStyle = pal.rain; ctx.fill()
        ctx.clip()
        texture.forEach(([a, b, c], i) => {
            const x = isl.x + (a - 0.5) * isl.rx * 2, y = isl.y + (b - 0.5) * isl.ry * 2
            if (i < 40) wash(ctx, x, y, 50 + c * 140, c > 0.6 ? pal.rainLight : c > 0.3 ? pal.rainDark : pal.palmLeafLight, 0.4)
            else { ctx.fillStyle = withAlpha(c > 0.5 ? pal.rainLight : pal.rainDark, 0.5); ctx.beginPath(); ctx.ellipse(x, y, 2.4, 1.3, 0, 0, Math.PI * 2); ctx.fill() }
        })
        // dark basalt round the volcano
        volcanoes.forEach((v) => {
            if (!inEllipse(isl, v.x, v.y)) return
            blobPath(ctx, v.x, v.y - 10, v.s * 1.25, v.s * 0.55, v.seed, 0.18, 26)
            ctx.fillStyle = withAlpha(pal.basalt, 0.9); ctx.fill()
            blobPath(ctx, v.x, v.y - 10, v.s * 1.05, v.s * 0.45, v.seed + 1, 0.18, 26)
            ctx.fillStyle = pal.basaltLight; ctx.fill()
        })
        ctx.restore()
        // the soft edge where the green meets the sand
        blobPath(ctx, isl.x, isl.y, isl.rx - 60, isl.ry - 48, isl.seed + 2, 0.1, 40)
        ctx.strokeStyle = withAlpha(pal.rainDark, 0.6); ctx.lineWidth = 6; ctx.stroke()
        // the atoll's lagoon: calm, bright, shallow
        if (isl.lagoon) {
            blobPath(ctx, isl.x, isl.y, isl.lagoon.rx + 14, isl.lagoon.ry + 10, isl.seed + 3, 0.08, 36)
            ctx.fillStyle = pal.tropSand; ctx.fill()
            blobPath(ctx, isl.x, isl.y, isl.lagoon.rx, isl.lagoon.ry, isl.seed + 4, 0.08, 36)
            const g = ctx.createRadialGradient(isl.x, isl.y, 10, isl.x, isl.y, Math.max(isl.lagoon.rx, isl.lagoon.ry))
            g.addColorStop(0, pal.lagoonDeep); g.addColorStop(1, pal.lagoon)
            ctx.fillStyle = g; ctx.fill()
            ctx.strokeStyle = withAlpha(pal.foam, 0.8); ctx.lineWidth = 2; ctx.stroke()
        }
        // shells and starfish on the sand
        const rs = rng(isl.seed + 9)
        for (let i = 0; i < 90; i++) {
            const a = rs() * Math.PI * 2, k = 0.86 + rs() * 0.16, kind = rs(), s = 1.2 + rs() * 1.6
            const x = isl.x + Math.cos(a) * (isl.rx - 25) * k, y = isl.y + Math.sin(a) * (isl.ry - 20) * k
            if (kind < 0.08) star(ctx, x, y, 4 + s, pal.reef)
            else { ctx.fillStyle = kind < 0.4 ? "#F6E7DA" : withAlpha(pal.tropSandDark, 0.8); ctx.beginPath(); ctx.arc(x, y, s, 0, Math.PI * 2); ctx.fill() }
        }
    }

    // --- nature ------------------------------------------------------------------------

    // `keep` clears what stands where something newer came (the railway),
    // after growing, so nothing else moves.
    function grow({ clearOf, spaced, note, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = []
        if (!active) return { sprites, footprints, trees, smalls, volcanoes }
        const r = rng(seed + 720)
        volcanoes.forEach((v) => {
            sprites.push({ x: v.x, y: v.y, box: [-v.s * 1.2, -v.s * 1.0, v.s * 1.2, 12], kind: "mountain", sway: 0, paint(ctx, pal) { paintVolcano(ctx, pal, v.s, v.seed) } })
            footprints.push({ circle: true, x: v.x, y: v.y - v.s * 0.1, r: v.s * 0.62 })
            footprints.push({ circle: true, x: v.x - v.s * 0.62, y: v.y - v.s * 0.04, r: v.s * 0.34 })
            footprints.push({ circle: true, x: v.x + v.s * 0.62, y: v.y - v.s * 0.04, r: v.s * 0.34 })
        })
        islands.forEach((isl) => {
            // coconut palms along the beach, leaning out over the water
            const ring = Math.round((isl.rx + isl.ry) / 34)
            for (let i = 0; i < ring; i++) {
                const a = (i / ring) * Math.PI * 2 + r() * 0.2, k = 0.8 + r() * 0.1, s = 17 + r() * 8, sd = Math.floor(r() * 1e6), skip = r()
                const x = isl.x + Math.cos(a) * (isl.rx - 40) * k, y = isl.y + Math.sin(a) * (isl.ry - 32) * k
                if (skip < 0.25 || nearVolcano(x, y, 40) || !clearOf(x, y, 10) || !spaced(x, y, s * 0.8)) continue
                const t = { x, y, s, lean: Math.cos(a) * 0.35, seed: sd }
                trees.push(t); note(t)
            }
            // and a scatter through the interior
            const inner = Math.round((isl.rx * isl.ry) / 9000)
            for (let i = 0; i < inner * 3 && i < 400; i++) {
                const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 0.7, s = 16 + r() * 9, sd = Math.floor(r() * 1e6), lean = (r() - 0.5) * 0.3
                const x = isl.x + Math.cos(a) * isl.rx * d, y = isl.y + Math.sin(a) * isl.ry * d
                if (!islandAt(x, y, 70) || nearVolcano(x, y, 60) || !clearOf(x, y, 10) || !spaced(x, y, s)) continue
                const t = { x, y, s, lean, seed: sd }
                trees.push(t); note(t)
            }
            // hibiscus bushes and black rocks
            for (let i = 0; i < 14; i++) {
                const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 0.75, s = 9 + r() * 6, sd = Math.floor(r() * 1e6), rock = r() < 0.35
                const x = isl.x + Math.cos(a) * isl.rx * d, y = isl.y + Math.sin(a) * isl.ry * d
                if (!islandAt(x, y, 50) || nearVolcano(x, y, 20) || !clearOf(x, y, 6) || !spaced(x, y, s)) continue
                const o = { x, y, s, rock, seed: sd }
                smalls.push(o); note(o)
            }
        })
        for (let i = trees.length - 1; i >= 0; i--) if (!keep(trees[i])) trees.splice(i, 1)
        for (let i = smalls.length - 1; i >= 0; i--) if (!keep(smalls[i])) smalls.splice(i, 1)
        trees.forEach((t) => {
            sprites.push({ x: t.x, y: t.y, box: [-t.s * 2.2, -t.s * 4.2, t.s * 2.2, 6], kind: "tree", sway: 1.2, seed: t.seed, paint(ctx, pal) { paintPalm(ctx, pal, t.s, t.seed, t.lean) } })
            footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * 0.22 + 3 })
        })
        smalls.forEach((o) => {
            if (o.rock) {
                sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.4, -o.s * 1.3, o.s * 1.4, o.s * 0.8 + 4], kind: "rock", sway: 0, paint(ctx, pal) { paintRock(ctx, basaltPal(pal), o.s, o.seed) } })
                footprints.push({ circle: true, x: o.x, y: o.y - 2, r: o.s * 0.7 })
            } else sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.7, -o.s * 2.2, o.s * 1.7, o.s * 0.8 + 4], kind: "bush", sway: 0.4, paint(ctx, pal) { paintBush(ctx, hibiscusPal(pal), o.s, o.seed) } })
        })
        return { sprites, footprints, trees, smalls, volcanoes }
    }

    function paintShadows(ctx, pal, vis, grown) {
        grown.volcanoes.forEach((v) => { if (vis(v.x, v.y, v.s * 1.6)) softShadow(ctx, v.x + v.s * 0.3, v.y + 6, v.s * 1.25, v.s * 0.3, pal.shadowAlpha * 1.1, pal.shadow) })
        grown.trees.forEach((t) => { if (vis(t.x, t.y, t.s * 3)) softShadow(ctx, t.x + t.s * (0.6 + t.lean * 2), t.y + 2, t.s * 1.3, t.s * 0.45, pal.shadowAlpha, pal.shadow) })
        grown.smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha * 0.8, pal.shadow) })
    }

    // Hibiscus flowers and grass tufts in the islands' interiors.
    function paintDetails(ctx, pal, vis, clearOf) {
        if (!active) return
        const r = rng(seed + 706)
        islands.forEach((isl) => {
            const count = Math.round((isl.rx * isl.ry) / 1500)
            for (let i = 0; i < count; i++) {
                const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 0.8, kind = r(), h = 4 + r() * 4
                const x = isl.x + Math.cos(a) * isl.rx * d, y = isl.y + Math.sin(a) * isl.ry * d
                if (!vis(x, y, 12) || !islandAt(x, y, 70) || nearVolcano(x, y, 10) || !clearOf(x, y, -12)) continue
                if (kind < 0.06) flower(ctx, x, y, kind < 0.03 ? pal.bloom : pal.bloom2)
                else { ctx.lineWidth = 1.1; ctx.globalAlpha = 0.5; paintTuft(ctx, kind < 0.5 ? pal.rainLight : pal.rainDark, x, y, h); ctx.globalAlpha = 1 }
            }
        })
    }

    return { active, islands, islandAt, inWater, volcanoes, nearVolcano, biome, paintShore, paintAfterSea, grow, paintShadows, paintDetails, shore }
}

// ---- painters -------------------------------------------------------------------------

const basaltCache = new Map()
function basaltPal(pal) {
    if (!basaltCache.has(pal)) basaltCache.set(pal, { ...pal, stone: pal.basalt, stoneLight: pal.basaltLight, stoneDark: pal.basaltDark })
    return basaltCache.get(pal)
}
const hibiscusCache = new Map()
function hibiscusPal(pal) {
    if (!hibiscusCache.has(pal)) hibiscusCache.set(pal, { ...pal, leaf: pal.palmLeaf, leafDark: pal.palmLeafDark, leafLight: pal.palmLeafLight, berry: pal.bloom, flowers: [pal.bloom, pal.bloom2, "#EE9AA8"] })
    return hibiscusCache.get(pal)
}

// A coconut palm: a ringed trunk curving up (leaning by `lean`), a crown of
// long feathered fronds drooping round it, and coconuts under the crown.
export function paintPalm(ctx, pal, s, seed, lean = 0) {
    const r = rng(seed)
    const ink = pal.ink
    const h = s * 3.4, topX = lean * s * 3 + (r() - 0.5) * s * 0.4, topY = -h
    const cx = topX * 0.35, cy = -h * 0.55
    // trunk: a tapering curve, ringed
    const seg = 9
    const at = (t) => [(1 - t) * (1 - t) * 0 + 2 * (1 - t) * t * cx + t * t * topX, (1 - t) * (1 - t) * 0 + 2 * (1 - t) * t * cy + t * t * topY]
    ctx.lineCap = "round"
    for (let i = 0; i < seg; i++) {
        const [ax, ay] = at(i / seg), [bx, by] = at((i + 1) / seg), w = s * (0.22 - (i / seg) * 0.09)
        line(ctx, [[ax, ay], [bx, by]], ink, w * 2 + 2.4)
        line(ctx, [[ax, ay], [bx, by]], i % 2 ? pal.palmTrunk : mix(pal.palmTrunk, "#000000", 0.12), w * 2)
    }
    // fronds: long arcs with little leaflets, the lower ones drooping
    const fronds = 8 + Math.floor(r() * 3)
    for (let i = 0; i < fronds; i++) {
        const a = (i / fronds) * Math.PI * 2 + r() * 0.3
        const len = s * (1.6 + r() * 0.5), droop = s * (0.5 + r() * 0.5) * (Math.sin(a) > 0 ? 1.4 : 0.7)
        const ex = topX + Math.cos(a) * len, ey = topY + Math.sin(a) * len * 0.45 + droop
        const mx = topX + Math.cos(a) * len * 0.55, my = topY + Math.sin(a) * len * 0.25 - s * 0.3
        const dark = Math.cos(a) > 0.3
        ctx.strokeStyle = ink; ctx.lineWidth = 3.4
        ctx.beginPath(); ctx.moveTo(topX, topY); ctx.quadraticCurveTo(mx, my, ex, ey); ctx.stroke()
        ctx.strokeStyle = dark ? pal.palmLeafDark : pal.palmLeaf; ctx.lineWidth = 2.2
        ctx.beginPath(); ctx.moveTo(topX, topY); ctx.quadraticCurveTo(mx, my, ex, ey); ctx.stroke()
        // leaflets
        for (let k = 1; k < 10; k++) {
            const t = k / 10
            const px = (1 - t) * (1 - t) * topX + 2 * (1 - t) * t * mx + t * t * ex, py = (1 - t) * (1 - t) * topY + 2 * (1 - t) * t * my + t * t * ey
            const nx = -(ey - topY), ny = ex - topX, m = Math.hypot(nx, ny) || 1
            const leafLen = s * 0.55 * (1 - t * 0.6)
            ;[-1, 1].forEach((side) => line(ctx, [[px, py], [px + (nx / m) * leafLen * side * 0.8, py + (ny / m) * leafLen * side * 0.8 + leafLen * 0.5]], side > 0 && !dark ? pal.palmLeafLight : pal.palmLeafDark, 1.6))
        }
    }
    // coconuts
    ;[[-4, 4], [4, 5], [0, 8]].forEach(([dx, dy]) => inkEllipse(ctx, topX + dx, topY + dy, 3.6, 3.4, pal.night ? "#4E4030" : "#7A5A30", ink, 1))
}

// The volcano: a broad dark cone with a flat, broken crater lit from inside,
// lava channels down its flanks, lighter ridges, and green creeping up its
// foot.
export function paintVolcano(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    const h = s * 0.82, rim = s * 0.34
    const outline = []
    const steps = 14
    for (let i = 0; i <= steps; i++) {
        const t = i / steps, x = -s + t * s * 2
        const y = Math.abs(x) < rim ? -h + (r() - 0.5) * 8 : -h * Math.max(0, 1 - (Math.abs(x) - rim) / (s - rim)) ** 0.8 + (r() - 0.5) * 6
        outline.push([x, i === 0 || i === steps ? 0 : y])
    }
    const trace = () => { ctx.beginPath(); outline.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath() }
    trace()
    const g = ctx.createLinearGradient(-s, -h, s, 0)
    g.addColorStop(0, pal.basaltLight); g.addColorStop(0.5, pal.basalt); g.addColorStop(1, pal.basaltDark)
    ctx.fillStyle = g; ctx.fill()
    ctx.save(); trace(); ctx.clip()
    // shade down the right flank
    ctx.fillStyle = withAlpha(pal.basaltDark, 0.8)
    ctx.beginPath(); ctx.moveTo(rim * 0.2, -h); ctx.lineTo(s * 1.1, -h); ctx.lineTo(s * 1.1, 0); ctx.lineTo(s * 0.25, 0); ctx.closePath(); ctx.fill()
    hatch(ctx, [0, -h, s * 1.1, h], mix(pal.basaltDark, "#000000", 0.3), { seed, angle: -1.2, gap: 3.4, alpha: 0.5 })
    // ridges and lava channels from the crater down
    for (let i = 0; i < 9; i++) {
        const sx = (r() - 0.5) * rim * 1.6, bend = (r() - 0.5) * s * 0.3, ex = sx * 2.4 + bend, lava = i % 3 === 0
        const pts = [[sx, -h + 4], [sx + bend * 0.4, -h * 0.55], [ex, -h * 0.12 - r() * h * 0.1]]
        if (lava) { line(ctx, pts, withAlpha(pal.lava, 0.95), 3.6); line(ctx, pts, withAlpha(pal.lavaGlow, 0.9), 1.4) } else line(ctx, pts, withAlpha(pal.basaltLight, 0.6), 1.6)
    }
    // green creeping up the foot
    for (let i = 0; i < 18; i++) {
        const x = (r() - 0.5) * s * 1.9, y = -r() * h * 0.22
        if (Math.abs(x) > s * (1 - (-y / h) * 0.9)) continue
        inkEllipse(ctx, x, y, 8 + r() * 10, 5 + r() * 5, r() < 0.5 ? pal.palmLeaf : pal.rainDark, withAlpha(ink, 0.35), 0.8)
    }
    ctx.restore()
    // the crater: a glowing slot on the flat top
    ctx.beginPath(); ctx.ellipse(0, -h + 2, rim * 0.85, rim * 0.22, 0, 0, Math.PI * 2)
    ctx.fillStyle = pal.basaltDark; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.stroke()
    ctx.beginPath(); ctx.ellipse(0, -h + 4, rim * 0.6, rim * 0.12, 0, 0, Math.PI * 2)
    const lg = ctx.createRadialGradient(0, -h + 4, 1, 0, -h + 4, rim * 0.6)
    lg.addColorStop(0, pal.lavaGlow); lg.addColorStop(1, pal.lava)
    ctx.fillStyle = lg; ctx.fill()
    ctx.beginPath(); outline.slice(1, steps).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
    ctx.strokeStyle = ink; ctx.lineWidth = 1.9; ctx.lineJoin = "round"; ctx.stroke()
}

function flower(ctx, x, y, color) {
    ctx.fillStyle = color
    for (let p = 0; p < 5; p++) { const a = (p / 5) * Math.PI * 2; ctx.beginPath(); ctx.ellipse(x + Math.cos(a) * 2.6, y + Math.sin(a) * 2.6, 2.4, 1.6, a, 0, Math.PI * 2); ctx.fill() }
    ctx.fillStyle = "#F6E7A0"; ctx.beginPath(); ctx.arc(x, y, 1.1, 0, Math.PI * 2); ctx.fill()
}

function star(ctx, x, y, r, fill) {
    ctx.beginPath()
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i / 10) * Math.PI * 2, rad = i % 2 ? r * 0.42 : r; ctx.lineTo(x + Math.cos(a) * rad, y + Math.sin(a) * rad) }
    ctx.closePath(); ctx.fillStyle = fill; ctx.fill()
}
